import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BadgeComponent } from '../badge-reusable/badge-reusable';
import { ReusableCheckbox } from '../checkbox-reusable/checkbox-reusable';
import { TooltipDirective } from '../tooltip/tooltip.directive';
import { ButtonComponent } from '../button-reusable/button-reusable';
 
export type TableColumnType = 'text' | 'avatar' | 'status' | 'actions' | 'badge';
export type TableSortDirection = 'asc' | 'desc';
export type TableActionType = 'view' | 'edit' | 'delete' | 'active' | 'inactive' | 'renew' | 'resend-invite' | 'button';
export type TableBadgeVariant = 'neutral' | 'red' | 'amber' | 'green' | 'blue' | 'indigo' | 'purple' | 'orange';

export interface TableActionButtonConfig {
  label: string;
  variant?: 'primary' | 'success' | 'danger' | 'neutral' | 'amber';
  appearance?: 'filled' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  icon?: { svg: string };
  iconPosition?: 'left' | 'right';
  disabled?: boolean;
}
 
export interface TableCellTemplateContext<T> {
  $implicit: T;
  row: T;
  column: TableColumnConfig<T>;
  value: unknown;
  rowIndex: number;
}
 
export interface AvatarColumnConfig<T> {
  imageKey?: keyof T | string;
  nameKey?: keyof T | string;
  emailKey?: keyof T | string;
  showStatusDot?: boolean;
  statusKey?: keyof T | string;
  statusActiveValues?: unknown[];
  /**
   * When true, the avatar name becomes a clickable link that emits a `view`
   * action (same as the row's view control), so clicking the name opens the
   * record. Opt-in per column.
   */
  clickableName?: boolean;
}
 
export interface StatusColorMapEntry {
  dot: string;
  border: string;
  bg: string;
  text: string;
}
 
export interface StatusColumnConfig<T> {
  valueKey?: keyof T | string;
  activeValues?: unknown[];
  activeLabel?: string;
  inactiveLabel?: string;
  colorMap?: Record<string, StatusColorMapEntry>;
}
 
export interface ActionsColumnConfig {
  mode?: 'inline' | 'dropdown';
  actions?: TableActionType[];
  statusKey?: string;
  statusActiveValues?: unknown[];
  hideActionsWhenActive?: TableActionType[];
  hideActionsWhenInactive?: TableActionType[];
  actionLabelResolver?: (action: TableActionType, row: any) => string;
  actionVisibilityResolver?: (action: TableActionType, row: any) => boolean;
  actionDisabledResolver?: (action: TableActionType, row: any) => boolean;
  actionTooltipResolver?: (action: TableActionType, row: any) => string;
  /** Config for custom button actions */
  actionButtonConfigResolver?: (action: TableActionType, row: any) => TableActionButtonConfig;
}
 
export interface BadgeColumnConfig<T> {
  valueKey?: keyof T | string;
  textKey?: keyof T | string;
  variantKey?: keyof T | string;
  variantMap?: Record<string, TableBadgeVariant>;
  statusVariantMap?: Record<string, TableBadgeVariant>;
  statusVariantFallback?: TableBadgeVariant;
  size?: 'sm' | 'md';
  bordered?: boolean;
  flat?: boolean;
  dot?: boolean;
}
 
export interface TableColumnConfig<T = Record<string, unknown>> {
  key: keyof T | string;
  label: string;
  headerTemplate?: TemplateRef<{ column: TableColumnConfig<T> }>;
  type?: TableColumnType;
  sortable?: boolean;
  sortKey?: keyof T | string;
  sortAccessor?: (row: T) => string | number | boolean | Date | null | undefined;
  searchAccessor?: (row: T) => string;
  className?: string;
  headerClassName?: string;
  avatarConfig?: AvatarColumnConfig<T>;
  statusConfig?: StatusColumnConfig<T>;
  badgeConfig?: BadgeColumnConfig<T>;
  actionsConfig?: ActionsColumnConfig;
  textClassName?: string;
  template?: TemplateRef<TableCellTemplateContext<T>>;
}
 
export interface TableSortState {
  key: string | null;
  direction: TableSortDirection | null;
}
 
export interface TableActionEvent<T> {
  action: TableActionType;
  row: T;
  column: TableColumnConfig<T>;
}
 
export interface TablePageChangeEvent {
  page: number;
  pageSize: number;
}

export type TablePageSummaryMode = 'default' | 'compact';

interface TableActionMenuPosition {
  top: number;
  left: number;
}
 
@Component({
  selector: 'app-table-reusable',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, ReusableCheckbox, BadgeComponent, TooltipDirective, ButtonComponent],
  templateUrl: './table-reusable.html',
  styleUrl: './table-reusable.css',
})
export class TableReusable implements OnChanges {
  @Input() data: any[] = [];
  @Input() columns: TableColumnConfig<any>[] = [];
  @Input() loading = false;
  @Input() stickyHeader = true;
  @Input() searchPlaceholder = 'Search...';
  @Input() emptyStateMessage = 'No records found';
  @Input() currentPage = 1;
  @Input() pageSize = 10;
  @Input() pageSizeOptions: number[] = [10, 20, 50];
  @Input() serverSidePagination = false;
  @Input() totalRecords: number | null = null;
  @Input() rowIdKey: keyof any | string | null = null;
  @Input() searchQuery = '';
  @Input() selectedRowIds: Array<string | number> = [];
  @Input() selectAllChecked = false;
  @Input() selectAllDisabled = false;
  @Input() pageSummaryMode: TablePageSummaryMode = 'default';
  @Input() showPageSizeSelector = true;
  @Input() showViewAllLink = false;
  @Input() viewAllLabel = 'View all';
 
  @Output() action = new EventEmitter<TableActionEvent<any>>();
  @Output() sortChange = new EventEmitter<TableSortState>();
  @Output() pageChange = new EventEmitter<TablePageChangeEvent>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() selectAllChange = new EventEmitter<boolean>();
  @Output() rowSelectionChange = new EventEmitter<{ id: string | number; checked: boolean }>();
  @Output() viewAll = new EventEmitter<void>();
  paginatedRows: any[] = [];
  totalItems = 0;
  totalPages = 1;
  visiblePageNumbers: number[] = [];
  internalSearch = '';
 
  get skeletonRows(): undefined[] {
    const count = Number.isFinite(this.pageSize) && this.pageSize > 0 ? this.pageSize : 10;
    return Array.from({ length: count });
  }
  readonly defaultActions: TableActionType[] = ['view', 'edit', 'delete'];
 
  sortState: TableSortState = {
    key: null,
    direction: null,
  };
 
  openMenuKey: string | null = null;
  openMenuPosition: TableActionMenuPosition | null = null;
  openMenuRow: any | null = null;
  openMenuColumn: TableColumnConfig<any> | null = null;
  private selectedRowIdSet = new Set<string>();
 
  constructor(private readonly hostElement: ElementRef<HTMLElement>) {}
 
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchQuery']) {
      this.internalSearch = this.searchQuery ?? '';
    }
 
    if (changes['pageSize'] && this.pageSize < 1) {
      this.pageSize = 10;
    }

    if (changes['currentPage'] && (!Number.isFinite(this.currentPage) || this.currentPage < 1)) {
      this.currentPage = 1;
    }
 
    if (changes['selectedRowIds']) {
      this.selectedRowIdSet = new Set((this.selectedRowIds ?? []).map((id) => String(id)));
    }
 
    this.recomputeRows();
  }
 
  onToggleSelectAll(checked: boolean): void {
    this.selectAllChange.emit(checked);
  }
 
  onToggleRowSelection(row: any, checked: boolean): void {
    const id = this.getRowId(row);
    if (id === null) {
      return;
    }
 
    this.rowSelectionChange.emit({ id, checked });
  }
 
  isRowSelected(row: any): boolean {
    const id = this.getRowId(row);
    return id !== null && this.selectedRowIdSet.has(String(id));
  }
 
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.hostElement.nativeElement.contains(event.target as Node)) {
      this.resetActionMenu();
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.resetActionMenu();
  }
 
  toggleSort(column: TableColumnConfig<any>): void {
    if (!column.sortable) {
      return;
    }
 
    const key = String(column.sortKey ?? column.key);
 
    if (this.sortState.key !== key) {
      this.sortState = { key, direction: 'asc' };
    } else if (this.sortState.direction === 'asc') {
      this.sortState = { key, direction: 'desc' };
    } else {
      this.sortState = { key: null, direction: null };
    }
 
    this.sortChange.emit(this.sortState);
 
    if (!this.serverSidePagination) {
      this.recomputeRows();
    }
  }
 
  onPageSizeChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const nextValue = Number(target.value);
 
    this.pageSize = Number.isFinite(nextValue) && nextValue > 0 ? nextValue : 10;
    this.currentPage = 1;
 
    if (!this.serverSidePagination) {
      this.recomputeRows();
    }
 
    this.emitPageChange();
  }
 
  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) {
      return;
    }
 
    this.currentPage = page;
 
    if (!this.serverSidePagination) {
      this.recomputeRows();
    }
 
    this.emitPageChange();
  }
 
  nextPage(): void {
    this.goToPage(this.currentPage + 1);
  }
 
  previousPage(): void {
    this.goToPage(this.currentPage - 1);
  }
 
  emitAction(action: TableActionType, row: any, column: TableColumnConfig<any>): void {
    this.action.emit({ action, row, column });
    this.resetActionMenu();
  }

  getActionButtonConfig(action: TableActionType, row: any, column: TableColumnConfig<any>): TableActionButtonConfig {
    const resolver = column.actionsConfig?.actionButtonConfigResolver;
    if (resolver) {
      return resolver(action, row);
    }
    return { label: 'Action', variant: 'primary', appearance: 'filled', size: 'sm' };
  }
 
  toggleActionMenu(event: MouseEvent, row: any, rowIndex: number, column: TableColumnConfig<any>): void {
    event.stopPropagation();
    const key = this.getMenuKey(row, rowIndex, column);

    if (this.openMenuKey === key) {
      this.resetActionMenu();
      return;
    }

    const trigger = event.currentTarget as HTMLElement | null;
    if (!trigger) {
      this.openMenuKey = key;
      this.openMenuPosition = null;
      this.openMenuRow = row;
      this.openMenuColumn = column;
      return;
    }

    const actions = this.getRowActions(row, column);
    const estimatedMenuHeight = Math.max(44, actions.length * 34 + 8);
    const estimatedMenuWidth = 128;
    const viewportPadding = 8;
    const gapFromTrigger = 8;
    const triggerRect = trigger.getBoundingClientRect();

    const overflowBottom = triggerRect.bottom + gapFromTrigger + estimatedMenuHeight > window.innerHeight;
    let top = overflowBottom
      ? triggerRect.top - gapFromTrigger - estimatedMenuHeight
      : triggerRect.bottom + gapFromTrigger;
    let left = triggerRect.right - estimatedMenuWidth;

    top = Math.min(
      Math.max(viewportPadding, top),
      Math.max(viewportPadding, window.innerHeight - estimatedMenuHeight - viewportPadding)
    );
    left = Math.min(
      Math.max(viewportPadding, left),
      Math.max(viewportPadding, window.innerWidth - estimatedMenuWidth - viewportPadding)
    );

    this.openMenuKey = key;
    this.openMenuPosition = { top, left };
    this.openMenuRow = row;
    this.openMenuColumn = column;
  }
 
  closeActionMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.resetActionMenu();
  }
 
  closeAllActionMenus(): void {
    this.resetActionMenu();
  }
 
  isMenuOpen(row: any, rowIndex: number, column: TableColumnConfig<any>): boolean {
    return this.openMenuKey === this.getMenuKey(row, rowIndex, column);
  }
 
  shouldOpenMenuUpward(rowIndex: number): boolean {
    const remainingRows = this.paginatedRows.length - rowIndex - 1;
    return remainingRows < 2;
  }
 
  getActionMode(column: TableColumnConfig<any>): 'inline' | 'dropdown' {
    return column.actionsConfig?.mode === 'dropdown' ? 'dropdown' : 'inline';
  }

  isCenterAlignedColumn(column: TableColumnConfig<any>): boolean {
    if (column.key === 'selection' || column.key === 'actions') {
      return true;
    }

    return column.type !== 'avatar';
  }
 
  getColumnActions(column: TableColumnConfig<any>): TableActionType[] {
    return column.actionsConfig?.actions?.length
      ? column.actionsConfig.actions
      : this.defaultActions;
  }
 
  /**
   * Inline-mode slots. Every configured action gets a slot with a `visible` flag,
   * so the template renders an empty spacer for actions hidden on this row. This
   * keeps each action in a fixed column position (e.g. a lone Delete stays aligned
   * under the Delete of Edit+Delete rows instead of sliding left into Edit's slot).
   * Toggle actions (`active`/`inactive`) share one slot since only one shows at a time.
   */
  getInlineActionSlots(
    row: any,
    column: TableColumnConfig<any>,
  ): Array<{ action: TableActionType; visible: boolean }> {
    const visibleActions = new Set(this.getRowActions(row, column));
    const slots: Array<{ action: TableActionType; visible: boolean }> = [];
    let toggleSlotAdded = false;

    for (const action of this.getColumnActions(column)) {
      // 'active' and 'inactive' are two faces of one toggle; only one shows at a
      // time, so collapse them into a single slot rather than reserving space for
      // both.
      if (action === 'active' || action === 'inactive') {
        if (toggleSlotAdded) {
          continue;
        }
        toggleSlotAdded = true;
        const shown = visibleActions.has('active') ? 'active' : 'inactive';
        slots.push({
          action: shown,
          visible: visibleActions.has('active') || visibleActions.has('inactive'),
        });
        continue;
      }

      slots.push({ action, visible: visibleActions.has(action) });
    }

    return slots;
  }

  getRowActions(row: any, column: TableColumnConfig<any>): TableActionType[] {
    const actions = this.getColumnActions(column);
    const rowIsActive = this.isRowActiveForActions(row, column);
    const hideWhenActive = column.actionsConfig?.hideActionsWhenActive ?? [];
    const hideWhenInactive = column.actionsConfig?.hideActionsWhenInactive ?? [];
    const visibilityResolver = column.actionsConfig?.actionVisibilityResolver;
 
    return actions.filter((action) => {
      if (visibilityResolver && !visibilityResolver(action, row)) {
        return false;
      }

      if (action === 'active') {
        return !rowIsActive;
      }
 
      if (action === 'inactive') {
        return rowIsActive;
      }
 
      if (rowIsActive && hideWhenActive.includes(action)) {
        return false;
      }
 
      if (!rowIsActive && hideWhenInactive.includes(action)) {
        return false;
      }
 
      return true;
    });
  }
 
  getSortIndicator(column: TableColumnConfig<any>): TableSortDirection | 'none' {
    if (!column.sortable) {
      return 'none';
    }
 
    const key = String(column.sortKey ?? column.key);
    if (this.sortState.key !== key) {
      return 'none';
    }
 
    return this.sortState.direction === 'asc' ? 'asc' : 'desc';
  }
 
  getCurrentRangeEnd(): number {
    return this.totalItems === 0 ? 0 : Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  getCurrentRangeStart(): number {
    return this.totalItems === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
  }
 
  getCellValue(row: any, column: TableColumnConfig<any>): unknown {
    return row[String(column.key) as keyof any];
  }

  getCellText(row: any, column: TableColumnConfig<any>): string {
    const value = this.getCellValue(row, column);
    return value === null || value === undefined ? '' : String(value).trim();
  }

  getCellTooltip(row: any, column: TableColumnConfig<any>): string | null {
    const resolvedType = column.type ?? 'text';

    if (column.key === 'actions' || column.key === 'selection' || resolvedType === 'actions') {
      return null;
    }

    if (resolvedType === 'avatar') {
      const name = this.getAvatarName(row, column).trim();
      const secondary = this.getAvatarSecondaryText(row, column).trim();
      const tooltip = [name, secondary].filter(Boolean).join(' - ');
      return tooltip || null;
    }

    if (resolvedType === 'status') {
      return this.getStatus(row, column).label || null;
    }

    if (resolvedType === 'badge') {
      return this.getBadgeText(row, column) || null;
    }

    const value = this.getCellValue(row, column);
    if (value === null || value === undefined) {
      return null;
    }

    const text = String(value).trim();
    return text || null;
  }

  getAvatarName(row: any, column: TableColumnConfig<any>): string {
    const avatarConfig = column.avatarConfig;
    const nameKey = String(avatarConfig?.nameKey ?? column.key);
    const nameValue = row[nameKey as keyof any];
 
    return typeof nameValue === 'string' && nameValue.trim()
      ? nameValue
      : String(nameValue ?? 'Unknown');
  }
 
  getAvatarEmail(row: any, column: TableColumnConfig<any>): string {
    const avatarConfig = column.avatarConfig;
    const emailKey = avatarConfig?.emailKey ? String(avatarConfig.emailKey) : '';
 
    if (!emailKey) {
      return '';
    }
 
    const emailValue = row[emailKey as keyof any];
    return typeof emailValue === 'string' ? emailValue : '';
  }

  getAvatarSecondaryText(row: any, column: TableColumnConfig<any>): string {
    const secondaryText = this.getAvatarEmail(row, column).trim();

    return secondaryText.replace(/^ID\s+/i, '');
  }
 
  getAvatarImage(row: any, column: TableColumnConfig<any>): string {
    const avatarConfig = column.avatarConfig;
    const imageKey = avatarConfig?.imageKey ? String(avatarConfig.imageKey) : '';
 
    if (!imageKey) {
      return '';
    }
 
    const imageValue = row[imageKey as keyof any];
    return typeof imageValue === 'string' ? imageValue : '';
  }
 
  getAvatarInitials(row: any, column: TableColumnConfig<any>): string {
    const name = this.getAvatarName(row, column);
    const parts = name
      .trim()
      .split(' ')
      .filter(Boolean)
      .slice(0, 2);
 
    if (parts.length === 0) {
      return 'NA';
    }
 
    return parts.map((item) => item.charAt(0).toUpperCase()).join('');
  }
 
  shouldShowAvatarStatusDot(column: TableColumnConfig<any>): boolean {
    return column.avatarConfig?.showStatusDot === true;
  }

  isAvatarNameClickable(column: TableColumnConfig<any>): boolean {
    return column.avatarConfig?.clickableName === true;
  }

  onAvatarNameClick(event: MouseEvent, row: any, column: TableColumnConfig<any>): void {
    event.stopPropagation();
    this.emitAction('view', row, column);
  }
 
  isAvatarActive(row: any, column: TableColumnConfig<any>): boolean {
    const avatarConfig = column.avatarConfig;
    const valueKey = String(avatarConfig?.statusKey ?? 'status');
    const value = row[valueKey as keyof any];
    const activeValues = avatarConfig?.statusActiveValues ?? [true, 'true', 'active', 'ACTIVE', 1, '1'];
 
    return this.isActiveValue(value, activeValues);
  }
 
  getStatus(row: any, column: TableColumnConfig<any>): { label: string; active: boolean; borderClass: string; bgClass: string; textClass: string; dotClass: string } {
    const statusConfig = column.statusConfig;
    const valueKey = String(statusConfig?.valueKey ?? column.key);
    const value = row[valueKey as keyof any];
    const normalizedValue = typeof value === 'string' ? value.trim() : String(value ?? '');
 
    const activeValues = statusConfig?.activeValues ?? [true, 'true', 'active', 'ACTIVE', 1, '1'];
    const active = this.isActiveValue(value, activeValues);
 
    const colorMap = statusConfig?.colorMap;
    if (colorMap && normalizedValue && colorMap[normalizedValue]) {
      const colors = colorMap[normalizedValue];
      return {
        active,
        label: normalizedValue,
        borderClass: colors.border,
        bgClass: colors.bg,
        textClass: colors.text,
        dotClass: colors.dot,
      };
    }
 
    return {
      active,
      label: active
        ? ((statusConfig?.activeLabel ?? normalizedValue) || 'Active')
        : ((statusConfig?.inactiveLabel ?? normalizedValue) || 'Inactive'),
      borderClass: active ? 'border-green400' : 'border-red400',
      bgClass: active ? 'bg-green100' : 'bg-red100',
      textClass: active ? 'text-green600' : 'text-red600',
      dotClass: active ? 'bg-green600' : 'bg-red600',
    };
  }
 
  getActionLabel(action: TableActionType, row: any, column: TableColumnConfig<any>): string {
    const resolver = column.actionsConfig?.actionLabelResolver;
    if (resolver) {
      return resolver(action, row);
    }
    return String(action);
  }

  getActionDisabled(action: TableActionType, row: any, column: TableColumnConfig<any>): boolean {
    const resolver = column.actionsConfig?.actionDisabledResolver;
    if (resolver) {
      return resolver(action, row);
    }
    return false;
  }

  getActionTooltip(action: TableActionType, row: any, column: TableColumnConfig<any>): string {
    const resolver = column.actionsConfig?.actionTooltipResolver;
    if (resolver) {
      return resolver(action, row);
    }
    return '';
  }
 
  getBadgeText(row: any, column: TableColumnConfig<any>): string {
    const textKey = String(column.badgeConfig?.valueKey ?? column.badgeConfig?.textKey ?? column.key);
    const value = row[textKey as keyof any];
    return value === null || value === undefined ? '' : String(value);
  }
 
  getBadgeVariant(row: any, column: TableColumnConfig<any>): TableBadgeVariant {
    const allowedVariants: TableBadgeVariant[] = [
      'neutral',
      'red',
      'amber',
      'green',
      'blue',
      'indigo',
      'purple',
      'orange',
    ];
 
    const badgeConfig = column.badgeConfig;
    if (badgeConfig?.variantKey) {
      const value = String(row[String(badgeConfig.variantKey) as keyof any] ?? '').toLowerCase();
      if ((allowedVariants as string[]).includes(value)) {
        return value as TableBadgeVariant;
      }
    }
 
    const textKey = String(badgeConfig?.valueKey ?? badgeConfig?.textKey ?? column.key);
    const statusValue = String(row[textKey as keyof any] ?? '').trim();
    const statusMap = badgeConfig?.variantMap ?? badgeConfig?.statusVariantMap;
 
    if (statusMap && statusValue) {
      const mappedVariant = statusMap[statusValue] ?? statusMap[statusValue.toLowerCase()];
      if (mappedVariant && (allowedVariants as string[]).includes(mappedVariant)) {
        return mappedVariant;
      }
    }
 
    const fallback = badgeConfig?.statusVariantFallback ?? 'neutral';
    return (allowedVariants as string[]).includes(fallback) ? fallback : 'neutral';
  }
 
  getBadgeSize(column: TableColumnConfig<any>): 'sm' | 'md' {
    return column.badgeConfig?.size ?? 'sm';
  }
 
  getBadgeBordered(column: TableColumnConfig<any>): boolean {
    return column.badgeConfig?.bordered ?? true;
  }
 
  getBadgeFlat(column: TableColumnConfig<any>): boolean {
    return column.badgeConfig?.flat ?? false;
  }
 
  getBadgeDot(column: TableColumnConfig<any>): boolean {
    return column.badgeConfig?.dot ?? false;
  }
 
  trackByColumn = (_: number, column: TableColumnConfig<any>): string => String(column.key);
 
  trackByRow = (index: number, row: any): string | number => {
    if (this.rowIdKey) {
      const value = row[String(this.rowIdKey) as keyof any];
      if (typeof value === 'string' || typeof value === 'number') {
        return value;
      }
    }
 
    return index;
  };
 
  private recomputeRows(): void {
    const normalizedPageSize = this.pageSize > 0 ? this.pageSize : 10;
    const rows = this.serverSidePagination
      ? [...this.data]
      : this.getSortedRows(this.getFilteredRows([...this.data]));
 
    this.totalItems = this.serverSidePagination
      ? Math.max(this.totalRecords ?? this.data.length, this.data.length)
      : rows.length;
    this.totalPages = Math.max(1, Math.ceil(this.totalItems / normalizedPageSize));
    this.currentPage = Math.min(Math.max(this.currentPage, 1), this.totalPages);
    this.visiblePageNumbers = this.getVisiblePageNumbers(this.currentPage, this.totalPages);
 
    if (this.serverSidePagination) {
      this.paginatedRows = rows;
      return;
    }
 
    const startIndex = (this.currentPage - 1) * normalizedPageSize;
    this.paginatedRows = rows.slice(startIndex, startIndex + normalizedPageSize);
  }
 
  private getFilteredRows(rows:any){
    if ( !this.internalSearch.trim()) {
      return rows;
    }
 
    const normalizedSearch = this.internalSearch.trim().toLowerCase();
 
    return rows.filter((row:any) =>
      this.columns.some((column) => {
        if (column.type === 'actions') {
          return false;
        }
 
        const value = column.searchAccessor
          ? column.searchAccessor(row)
          : this.getSearchableCellValue(row, column);
 
        return value.toLowerCase().includes(normalizedSearch);
      })
    );
  }
 
  private getSearchableCellValue(row: any, column: TableColumnConfig<any>): string {
    if (column.type === 'avatar') {
      const name = this.getAvatarName(row, column);
      const email = this.getAvatarEmail(row, column);
      return `${name} ${email}`.trim();
    }
 
    const value = this.getCellValue(row, column);
    return String(value ?? '');
  }
 
  private getSortedRows(rows: any[]): any[] {
    const activeSortKey = this.sortState.key;
    const activeSortDirection = this.sortState.direction;
 
    if (!activeSortKey || !activeSortDirection) {
      return rows;
    }
 
    const direction = activeSortDirection === 'asc' ? 1 : -1;
 
    return rows.sort((firstRow, secondRow) => {
      const firstValue = this.resolveSortValue(firstRow, activeSortKey);
      const secondValue = this.resolveSortValue(secondRow, activeSortKey);
 
      return this.compareValues(firstValue, secondValue) * direction;
    });
  }
 
  private resolveSortValue(row: any, sortKey: string): unknown {
    const column = this.columns.find((item) => String(item.sortKey ?? item.key) === sortKey);
 
    if (column?.sortAccessor) {
      return column.sortAccessor(row);
    }
 
    return row[sortKey as keyof any];
  }
 
  private compareValues(firstValue: unknown, secondValue: unknown): number {
    if (firstValue == null && secondValue == null) {
      return 0;
    }
 
    if (firstValue == null) {
      return 1;
    }
 
    if (secondValue == null) {
      return -1;
    }
 
    if (firstValue instanceof Date && secondValue instanceof Date) {
      return firstValue.getTime() - secondValue.getTime();
    }
 
    if (typeof firstValue === 'number' && typeof secondValue === 'number') {
      return firstValue - secondValue;
    }
 
    return String(firstValue).localeCompare(String(secondValue), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  }
 
  private getVisiblePageNumbers(currentPage: number, totalPages: number): number[] {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }
 
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, start + 4);
    const adjustedStart = Math.max(1, end - 4);
    const visibleRange = end - adjustedStart + 1;
 
    return Array.from({ length: visibleRange }, (_, index) => adjustedStart + index);
  }
 
  private getMenuKey(row: any, rowIndex: number, column: TableColumnConfig<any>): string {
    const identity = this.trackByRow(rowIndex, row);
    return `${String(column.key)}-${String(identity)}`;
  }
 
  private isActiveValue(value: unknown, activeValues: unknown[]): boolean {
    return activeValues.some((activeValue) => activeValue === value);
  }
 
  private isRowActiveForActions(row: any, column: TableColumnConfig<any>): boolean {
    const statusKey = column.actionsConfig?.statusKey ?? 'status';
    const activeValues = column.actionsConfig?.statusActiveValues ?? [true, 'true', 'active', 'ACTIVE', 1, '1'];
    const value = row[String(statusKey) as keyof any];
 
    return this.isActiveValue(value, activeValues);
  }
 
  private getRowId(row: any): string | number | null {
    if (!this.rowIdKey) {
      return null;
    }
 
    const value = row[String(this.rowIdKey) as keyof any];
    return typeof value === 'string' || typeof value === 'number' ? value : null;
  }
 
  private emitPageChange(): void {
    this.pageChange.emit({
      page: this.currentPage,
      pageSize: this.pageSize,
    });
  }

  protected onViewAll(): void {
    this.viewAll.emit();
  }

  private resetActionMenu(): void {
    this.openMenuKey = null;
    this.openMenuPosition = null;
    this.openMenuRow = null;
    this.openMenuColumn = null;
  }
 
}